/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect } from "react";
import React from "react";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import { NavLink } from "react-router-dom";
import { alpha } from "@mui/material/styles";

// Custom styles for DashboardNavbar
import { navbarMobileMenu } from "examples/Navbars/DashboardNavbar/styles";
import MicrolabLogo, { MICROLAB_NAVY } from "components/MicrolabLogo";
import MenuToggleIcon from "components/MenuToggleIcon";

// Browser/status-bar colours matching the header (closed) and the menu sheet (open).
const STATUS_BAR_COLORS = {
  light: "#ffffff",
  dark: "#0c0f14",
  menu: "#14325a",
};

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
} from "context";

function DashboardNavbar({ absolute: _absolute, light: _light, isMini }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, fixedNavbar, darkMode } = controller;

  useEffect(() => {
    // Keep the top bar static + transparent. The old sticky "frosted" AppBar
    // became an empty white floating block when scrolling (settings/notifications
    // were removed), so we no longer promote it on scroll.
    setTransparentNavbar(dispatch, true);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const menuOpen = !miniSidenav;

  // Tint the phone's status bar / browser chrome to match the header, or the
  // navy menu sheet while it is open. Restore the page default on unmount.
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta || isMini) return undefined;
    const previous = meta.getAttribute("content");
    let color = darkMode ? STATUS_BAR_COLORS.dark : STATUS_BAR_COLORS.light;
    if (menuOpen && window.matchMedia("(max-width: 1199.95px)").matches) {
      color = STATUS_BAR_COLORS.menu;
    }
    meta.setAttribute("content", color);
    return () => meta.setAttribute("content", previous);
  }, [darkMode, menuOpen, isMini]);

  // Desktop (xl+): permanent sidenav — no top menu chrome needed.
  if (isMini) return null;

  return (
    <AppBar
      position="sticky"
      color="inherit"
      elevation={0}
      sx={(theme) => ({
        display: { xs: "block", xl: "none" },
        top: 0,
        zIndex: theme.zIndex.appBar,
        mb: 1.5,
        // Sit in layout flow so content (tabs, cards) is never covered.
        backgroundColor: darkMode
          ? alpha(theme.palette.background.default, 0.88)
          : "rgba(255, 255, 255, 0.92)",
        backdropFilter: "blur(14px) saturate(1.2)",
        WebkitBackdropFilter: "blur(14px) saturate(1.2)",
        // Respect notched phones / home-indicator insets.
        paddingTop: "env(safe-area-inset-top, 0px)",
        // Tablet (md–xl): floating card inside DashboardLayout's gutters.
        width: "100%",
        mx: 0,
        border: `1px solid ${
          darkMode ? alpha("#fff", 0.08) : "rgba(15, 23, 42, 0.07)"
        }`,
        boxShadow: darkMode
          ? "0 4px 16px rgba(0,0,0,0.25)"
          : "0 4px 16px rgba(3, 4, 94, 0.06)",
        borderRadius: "16px",
        // Phones (<md): flush, edge-to-edge bar. Stretches into the scroll
        // container's padding (not clipped: overflow clips at the padding box)
        // so the status-bar theme colour runs straight into it.
        [theme.breakpoints.down("md")]: {
          width: "auto",
          marginLeft: theme.spacing(-1.25),
          marginRight: theme.spacing(-1.25),
          border: "none",
          borderBottom: `1px solid ${
            darkMode ? alpha("#fff", 0.08) : "rgba(15, 23, 42, 0.08)"
          }`,
          borderRadius: 0,
          boxShadow: "none",
          backgroundColor: darkMode ? theme.palette.background.default : "#ffffff",
        },
        // Matches DashboardLayout's wider 576–767px gutters.
        [theme.breakpoints.between("sm", "md")]: {
          marginLeft: theme.spacing(-3),
          marginRight: theme.spacing(-3),
        },
      })}
    >
      <Toolbar
        disableGutters
        sx={{
          minHeight: { xs: 56, sm: 60 },
          // ~16px visual inset on phones (the 44px button pads its icon by 11px).
          pl: 2,
          pr: { xs: 0.5, md: 1.25 },
          gap: 1.5,
          justifyContent: "space-between",
        }}
      >
        <Box
          component={NavLink}
          to="/"
          aria-label="Microlab home"
          sx={{ display: "flex", alignItems: "center", minWidth: 0, lineHeight: 0 }}
        >
          <MicrolabLogo
            height={{ xs: 26, sm: 28 }}
            textColor={darkMode ? "#f8fafc" : MICROLAB_NAVY}
          />
        </Box>

        <IconButton
          size="medium"
          disableRipple
          color="inherit"
          aria-label={menuOpen ? "Close navigation" : "Open navigation"}
          aria-expanded={menuOpen}
          sx={[
            navbarMobileMenu,
            {
              width: 44,
              height: 44,
              borderRadius: "50%",
              border: "none",
              backgroundColor: "transparent",
              color: darkMode ? "#f8fafc" : MICROLAB_NAVY,
              boxShadow: "none",
              flexShrink: 0,
              "&:hover": {
                backgroundColor: darkMode
                  ? alpha("#fff", 0.08)
                  : alpha(MICROLAB_NAVY, 0.06),
              },
              "&:focus-visible": {
                outline: `2px solid ${darkMode ? "#f8fafc" : MICROLAB_NAVY}`,
                outlineOffset: 2,
              },
              "&:active": {
                transform: "scale(0.94)",
              },
              transition: "background-color 0.18s ease, transform 0.12s ease",
            },
          ]}
          onClick={handleMiniSidenav}
        >
          <MenuToggleIcon open={menuOpen} />
        </IconButton>
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the props of DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
